import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useRichTextEditorContext } from "@mantine/tiptap";
import { saveAs } from "file-saver";
import { HTMLAttributes, useState } from "react";
import {
    generateDocxReport,
    ExportReportFileType,
    generatePdfReport,
} from "../../../api/report";
import useTestId from "../../../hooks/useTestId";
import { cn } from "../../../utils";
import Loader from "../../AppLoader";
import { getHTMLWithPageBreaks, printDocument } from "../utils";

export const ActionsMenu: React.FC<HTMLAttributes<HTMLButtonElement>> = ({
    ...props
}) => {
    const { editor } = useRichTextEditorContext();
    const [isSaving, setIsSaving] = useState(false);
    const testId = useTestId();

    if (!editor) {
        return null;
    }

    const exportReportHandler = async (fileType: ExportReportFileType) => {
        setIsSaving(true);

        try {
            if (fileType === "docx") {
                const res = await generateDocxReport({
                    testId,
                    html: editor.getHTML(),
                });
                if (!res) {
                    throw new Error("Failed to generate report");
                }
                const blob = await res.blob();

                if ("showSaveFilePicker" in window) {
                    const handle = await (window as any).showSaveFilePicker({
                        suggestedName: `Report.${fileType}`,
                        types: [
                            {
                                description: `${fileType.toUpperCase()} Document`,
                                accept: {
                                    [`application/${fileType}`]: [
                                        `.${fileType}`,
                                    ],
                                },
                            },
                        ],
                    });
                    const writable = await handle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                } else {
                    saveAs(blob, `Report.${fileType}`);
                }
            } else {
                generatePdfReport({
                    html: document.querySelector(".tiptap")?.innerHTML || "",
                });
            }
        } catch (error) {
            console.error(`Error saving ${fileType.toUpperCase()}:`, error);
            alert(
                `An error occurred while generating the ${fileType.toUpperCase()} file.`
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handlePrint = () => {
        printDocument(editor);
    };

    return (
        <Menu>
            <MenuButton
                {...props}
                className={cn(
                    "btn-primary size-10 xl:size-[52px] p-0 flex items-center justify-center disabled:opacity-50",
                    props.className
                )}
                disabled={isSaving}
            >
                {isSaving ? (
                    <div className="size-5">
                        <Loader />
                    </div>
                ) : (
                    <EllipsisVerticalIcon className="size-7 xl:size-8" />
                )}
            </MenuButton>
            <MenuItems
                transition
                anchor="bottom end"
                className="w-52 origin-top-right rounded-xl border border-gray-200 bg-white p-1 text-sm/6 transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
            >
                <MenuItem>
                    <button
                        onClick={() => exportReportHandler("pdf")}
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-black/5"
                    >
                        Export as PDF
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        onClick={() => exportReportHandler("docx")}
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-black/5 disabled:opacity-50"
                    >
                        Export as DOCX
                    </button>
                </MenuItem>
                <MenuItem>
                    <button
                        onClick={handlePrint}
                        className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 data-[focus]:bg-black/5"
                    >
                        Print
                    </button>
                </MenuItem>
            </MenuItems>
        </Menu>
    );
};
